<!--
  Redeem an invite link.

  The page shows what the invite is *for* before asking for anything: the email it was issued
  to and the role it carries. Neither is editable, and neither is sent back — the service takes
  both from the invite row, so a link cannot be redirected to another mailbox or redeemed at a
  higher role than it was issued for. Showing them is how the invitee checks the link is meant
  for them; letting them be edited would be how somebody else used it.

  An invalid link gets a plain explanation and no form. The service answers `valid: false` for
  unknown, spent, revoked and expired tokens alike (a 404 for one and a 200 for another would
  make this a probe for valid tokens), so the reason shown is the service's prose, not a guess.
-->
<script lang="ts">
  import { api } from '../lib/api/client';
  import { asProblem } from '../lib/api/problem';
  import type { InvitePreview } from '../lib/api/types';
  import { router } from '../lib/router.svelte';
  import { auth } from '../lib/stores/auth.svelte';

  interface Props {
    token: string;
  }

  const { token }: Props = $props();

  /** Matches `MIN_PASSWORD_LENGTH` in `api/security.py`. Checked there too; this is courtesy. */
  const MIN_PASSWORD = 12;

  let preview = $state<InvitePreview | null>(null);
  let loading = $state(true);
  let fullName = $state('');
  let password = $state('');
  let confirmation = $state('');
  let busy = $state(false);
  let error = $state<string | null>(null);

  $effect(() => {
    const current = token;
    let cancelled = false;
    loading = true;
    void (async () => {
      try {
        const result = current
          ? await api.previewInvite(current)
          : ({
              valid: false,
              email: null,
              role: null,
              expires_at: null,
              reason: 'This link is missing its token.',
            } satisfies InvitePreview);
        if (!cancelled) preview = result;
      } catch (caught) {
        if (!cancelled) error = asProblem(caught).message;
      } finally {
        if (!cancelled) loading = false;
      }
    })();
    return () => {
      cancelled = true;
    };
  });

  const mismatch = $derived(confirmation.length > 0 && confirmation !== password);
  const tooShort = $derived(password.length > 0 && password.length < MIN_PASSWORD);
  const submittable = $derived(
    !busy &&
      fullName.trim().length > 0 &&
      password.length >= MIN_PASSWORD &&
      confirmation === password,
  );

  async function submit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!submittable) return;
    error = null;
    busy = true;
    try {
      // Only the three fields. No email, no role — see the comment at the top of this file.
      const payload = await api.acceptInvite({
        token,
        full_name: fullName.trim(),
        password,
      });
      auth.accept(payload);
      router.go({ name: 'home', tab: 'start' });
    } catch (caught) {
      error = asProblem(caught).message;
    } finally {
      busy = false;
    }
  }
</script>

<div class="page">
  <div class="panel">
    <h1>Accept your invitation</h1>

    {#if loading}
      <p class="muted">Checking the link…</p>
    {:else if !preview?.valid}
      <p class="error" role="alert">
        {preview?.reason ?? error ?? 'This invite link is not valid.'}
      </p>
      <p class="muted small">
        Invite links are single-use and expire. Ask an administrator for a new one — reissuing
        is quick, and the old link stops working the moment they do.
      </p>
      <a href="#/login">Go to sign in</a>
    {:else}
      <p class="muted small">
        This invitation is for <strong>{preview.email}</strong>, as
        <strong>{preview.role}</strong>. Both come from the invitation and cannot be changed
        here.
      </p>

      <form onsubmit={submit}>
        <label>
          <span>Your name</span>
          <!-- svelte-ignore a11y_autofocus -->
          <input
            type="text"
            bind:value={fullName}
            required
            autofocus
            autocomplete="name"
            maxlength="128"
          />
          <span class="muted small">
            Shown to colleagues when you hold an image, and on every box you draw.
          </span>
        </label>

        <label>
          <span>Choose a password</span>
          <input type="password" bind:value={password} required autocomplete="new-password" />
          <span class="muted small" class:warn={tooShort}>
            At least {MIN_PASSWORD} characters. Length beats punctuation — a short memorable
            phrase of four or five words is both stronger and easier than <code>P@ssw0rd</code>.
          </span>
        </label>

        <label>
          <span>Confirm the password</span>
          <input
            type="password"
            bind:value={confirmation}
            required
            autocomplete="new-password"
          />
          {#if mismatch}
            <span class="warn small">The two passwords do not match.</span>
          {/if}
        </label>

        {#if error}
          <p class="error" role="alert">{error}</p>
        {/if}

        <button class="primary" type="submit" disabled={!submittable}>
          {busy ? 'Creating your account…' : 'Create my account'}
        </button>
      </form>
    {/if}
  </div>
</div>

<style>
  .page {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    padding: 48px 16px;
  }

  .panel,
  form {
    display: flex;
    flex-direction: column;
    gap: 14px;
    width: 100%;
    max-width: 420px;
  }

  h1 {
    margin: 0;
    font-size: 1.3rem;
  }

  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .error {
    margin: 0;
    padding: 8px 10px;
    border: 1px solid var(--danger, #b3261e);
    border-radius: 4px;
    font-size: 0.9rem;
  }

  .small {
    font-size: 0.82rem;
    line-height: 1.5;
  }

  .warn {
    color: var(--warn, #b8860b);
  }
</style>
